// Turning a Custom HTML/CSS block into a picture for the PowerPoint and PDF
// exports.
//
// Those two formats have no stylesheet. Left to the generic path a hand-written
// layout arrived as a column of unstyled sentences: the grid, the panels, the
// colours, the type scale — everything the author actually built — gone, with
// only the words surviving. That is a poor answer for a block whose entire
// purpose is that it looks a particular way.
//
// So the layout is rendered the way a browser renders it and handed to the
// exporters as a single full-bleed image. The technique is SVG's <foreignObject>
// wrapping the markup, loaded through an <img> and painted onto a <canvas>:
// entirely built-ins, no rasterizer dependency, and it reproduces the real CSS
// because it IS the browser's CSS engine doing the work.
//
// It fails closed. Anything that goes wrong — no DOM, a tainted canvas, an
// image that will not decode — returns null, and the caller falls back to the
// old text extraction. A degraded slide beats a broken export.

import { EDITOR_RESET_CSS, VIDEO_BASE_CSS, prepareCustomHtmlBlock } from './customCss';
import { sanitizeSlideLayoutHtml } from './sanitizeHtml';
import { EXPORT_SLIDE_W, EXPORT_CONTENT_H } from './slideFrames';

const CSS_PX_PER_IN = 96;

/**
 * Render at 2x the content region's CSS size. The picture is placed at roughly
 * 13.3in wide, so 1x would be visibly soft on a projector and at print
 * resolution; 2x is the usual retina trade between sharpness and file size.
 */
const RENDER_SCALE = 2;

/**
 * The CSS size of a slide's content region — the box the layout is *laid out*
 * in, which is a different thing from the size it is rasterized at.
 *
 * Laying out at the CSS size and letting the SVG viewBox do the 2x is what
 * keeps the export honest. Laying out directly at 2x would silently halve
 * every fixed-px measurement relative to the slide: `padding: 32px` would come
 * out looking like 16px next to the same layout in the app. Sizes in `cqw`
 * (what the block's brief recommends) are immune either way, but authors do
 * reach for px, and they should get what they wrote.
 */
export const LAYOUT_WIDTH = Math.round(EXPORT_SLIDE_W * CSS_PX_PER_IN);
export const LAYOUT_HEIGHT = Math.round(EXPORT_CONTENT_H * CSS_PX_PER_IN);

export const RENDER_WIDTH = LAYOUT_WIDTH * RENDER_SCALE;
export const RENDER_HEIGHT = LAYOUT_HEIGHT * RENDER_SCALE;
export const RENDER_ASPECT = RENDER_WIDTH / RENDER_HEIGHT;

/** The typography a slide inherits from the app before any custom rule runs. */
const BASE_STYLE = [
  'width: 100%',
  'height: 100%',
  "font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  'font-size: 16px',
  'line-height: 1.5',
  'color: #1f2937',
  '-webkit-font-smoothing: antialiased',
].join('; ');

/** XML text, not HTML text: a bare & or < ends the document early. */
const escapeXmlText = (value = '') =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;');

const escapeXmlAttr = (value = '') =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

/**
 * Re-serialize sanitized markup as XHTML.
 *
 * <foreignObject> content is parsed as XML, so an unclosed <img> or <br> — both
 * legal HTML — would abort the whole render. Parsing with the HTML parser and
 * serializing with XMLSerializer converts them.
 */
/**
 * Drop pictures the renderer is never going to fetch.
 *
 * An SVG loaded through an <img> is a closed document: the browser resolves no
 * external subresource inside it, by design. A remote `<img src="https://…">`
 * therefore does not render slowly or partially — it renders as nothing, and
 * the layout exports with a hole where the picture was. That bites video
 * posters in particular, since a thumbnail hosted by YouTube can only be
 * embedded as a data URL if the fetch for it was allowed to succeed.
 *
 * So a remote picture is removed and, if it was a clip's poster, its link
 * falls back to the text card the no-poster path already styles. A labelled
 * card beats an empty rectangle.
 */
const dropRemoteImages = (doc) => {
  doc.body.querySelectorAll('img[src]').forEach((img) => {
    if (/^data:/i.test(img.getAttribute('src') || '')) return;
    const link = img.closest('a.lp-video');
    img.remove();
    if (!link) return;
    link.setAttribute('class', `${link.getAttribute('class') || ''} lp-video--text`.trim());
    if (!link.textContent.trim()) {
      link.textContent = img.getAttribute('alt') || link.getAttribute('href') || '';
    }
  });
};

const toXhtml = (html) => {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  dropRemoteImages(doc);
  const holder = doc.createElementNS('http://www.w3.org/1999/xhtml', 'div');
  while (doc.body.firstChild) holder.appendChild(doc.body.firstChild);
  return new XMLSerializer().serializeToString(holder)
    // The wrapper itself is not wanted, only its contents.
    .replace(/^<div[^>]*>/, '')
    .replace(/<\/div>$/, '');
};

/**
 * The slide's own background, as CSS, for the formats that cannot carry
 * transparency.
 *
 * PowerPoint takes the PNG's alpha channel, so the raster is left transparent
 * and the slide's background shows through — which also avoids a seam where the
 * content region meets the header. The PDF writer here has no alpha: every
 * image is flattened onto white on its way to JPEG, so an unpainted layout
 * would punch a white rectangle through the slide's background. Painting the
 * background into the raster is what keeps the two formats looking the same.
 */
export const backgroundCss = (background) => {
  if (!background || typeof background !== 'object') return '';
  const parts = [];
  if (background.color) parts.push(`background-color: ${background.color}`);
  if (background.image) {
    parts.push(`background-image: url("${String(background.image).replace(/["\\]/g, '')}")`);
    parts.push('background-size: cover', 'background-position: center', 'background-repeat: no-repeat');
  }
  return parts.join('; ');
};

/** Compose the SVG document that carries one block's layout. */
export const buildCustomHtmlSvg = (block, { width = RENDER_WIDTH, height = RENDER_HEIGHT, background = null } = {}) => {
  const { html, css, imageVarsCss, deckScoped } = prepareCustomHtmlBlock(block);
  const safeHtml = sanitizeSlideLayoutHtml(html);
  if (!safeHtml.trim()) return null;

  const body = toXhtml(safeHtml);
  if (body === null) return null;

  // Deck-scoped rules are global in the app; here the block is the whole
  // document, so they apply either way.
  const sheet = [EDITOR_RESET_CSS, VIDEO_BASE_CSS, imageVarsCss, css].filter(Boolean).join('\n');
  const blockId = escapeXmlAttr(block?.id || 'block');

  // container-type on the wrapper is what makes cqw units resolve — the block's
  // brief tells authors to size everything in cqw, so without it an exported
  // layout would collapse to unstyled defaults.
  const rootStyle = [BASE_STYLE, backgroundCss(background)].filter(Boolean).join('; ');

  // The viewBox is the CSS layout box; width/height are the pixels asked for.
  // The renderer lays the HTML out in the former and scales it into the latter.
  const boxW = LAYOUT_WIDTH;
  const boxH = Math.round((LAYOUT_WIDTH * height) / width) || LAYOUT_HEIGHT;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${boxW} ${boxH}">`
    + `<foreignObject x="0" y="0" width="${boxW}" height="${boxH}">`
    + `<div xmlns="http://www.w3.org/1999/xhtml" style="${escapeXmlAttr(rootStyle)}">`
    + `<style>${escapeXmlText(sheet)}</style>`
    + `<div class="slide-custom-html" data-block="${blockId}" data-css-scope="${deckScoped ? 'deck' : 'slide'}"`
    + ` style="width: 100%; height: 100%; container-type: inline-size;">${body}</div>`
    + '</div></foreignObject></svg>';
};

/**
 * How long to wait for one layout to decode before giving up on it.
 *
 * The timeout is not belt-and-braces: an <img> pointed at an SVG the engine
 * will not render can fire neither `load` nor `error` — it simply never
 * settles. Without a deadline that hangs the whole export on one bad block,
 * which is far worse than the slide falling back to its text.
 */
const DECODE_TIMEOUT_MS = 15000;

const loadImage = (src, timeoutMs = DECODE_TIMEOUT_MS) => new Promise((resolve, reject) => {
  if (typeof document === 'undefined') { reject(new Error('no document')); return; }
  const img = document.createElement('img');
  let settled = false;
  const finish = (fn, value) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    img.onload = null;
    img.onerror = null;
    fn(value);
  };
  const timer = setTimeout(() => finish(reject, new Error('svg render timed out')), timeoutMs);
  img.onload = () => finish(resolve, img);
  img.onerror = () => finish(reject, new Error('svg failed to decode'));
  img.src = src;
});

/**
 * Rasterize one block. Returns a PNG data URL, or null when the layout is empty
 * or anything in the pipeline refuses.
 */
export const rasterizeCustomHtmlBlock = async (block, options = {}) => {
  const width = options.width || RENDER_WIDTH;
  const height = options.height || RENDER_HEIGHT;

  try {
    const svg = buildCustomHtmlSvg(block, { width, height, background: options.background });
    if (!svg) return null;
    if (typeof document === 'undefined') return null;

    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    const img = await loadImage(url, options.timeoutMs);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Left transparent: the slide's own background colour or image is painted
    // by the exporter underneath, exactly as the app composites them.
    ctx.drawImage(img, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl && dataUrl.startsWith('data:image/png') ? dataUrl : null;
  } catch (error) {
    console.warn('[customHtmlRaster] Falling back to text for a custom layout', error);
    return null;
  }
};

const isCustomHtmlBlock = (block) => block && block.type === 'custom-html';

/**
 * Attach a rendered picture to every custom-html block in a slide list.
 *
 * Returns a new list; the caller's slides are untouched. Blocks that cannot be
 * rendered are returned as they were, so the exporters fall back per block
 * rather than all-or-nothing.
 */
export const withCustomHtmlRenders = async (slides = [], options = {}) => {
  if (!Array.isArray(slides) || !slides.length) return slides;
  if (!slides.some((slide) => (slide?.blocks || []).some(isCustomHtmlBlock))) return slides;

  // `style` is the exporters' style payload; resolving the background here the
  // same way buildDeckModel does keeps the picture on the colour the slide will
  // actually be.
  const style = options.style || {};
  const resolveBackground = (slideId) =>
    (slideId && style.backgrounds?.[slideId]) || style.defaultBackground || null;

  return Promise.all(slides.map(async (slide) => {
    const blocks = slide?.blocks;
    if (!Array.isArray(blocks) || !blocks.some(isCustomHtmlBlock)) return slide;

    const background = options.opaque ? resolveBackground(slide?.slideId) : null;

    const nextBlocks = await Promise.all(blocks.map(async (block) => {
      if (!isCustomHtmlBlock(block)) return block;
      const renderedImage = await rasterizeCustomHtmlBlock(block, { ...options, background });
      return renderedImage ? { ...block, renderedImage } : block;
    }));

    return { ...slide, blocks: nextBlocks };
  }));
};
